import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { useNextDevServer } from "@v2/features/page-renderer/components/code-execution/context";
import { useVersionHistory } from "@v2/features/version-history/useVersionHistory";
import { useViewingVersionId } from "@v2/store/editorStore";
import { Eye, RotateCcw } from "lucide-react";

interface VersionButtonProps {
  fileVersionId: string;
}

export const VersionButton = ({ fileVersionId }: VersionButtonProps) => {
  const {
    handleRevertPageToVersion,
    handlePreviewVersion,
    isRevertingPageToVersion,
    isViewingVersion,
    isCurrentVersion,
  } = useVersionHistory({ fileVersionId });
  const viewingVersionId = useViewingVersionId();
  const { isSyncPending } = useNextDevServer();

  const getButtonState = () => {
    if (isViewingVersion) {
      if (isCurrentVersion) {
        return {
          icon: <RotateCcw size={12} />,
          onClick: handleRevertPageToVersion,
          disabled: true,
          isLoading: isRevertingPageToVersion,
          tooltipText: "You are already on this version",
        };
      }

      return {
        icon: <RotateCcw size={12} />,
        onClick: handleRevertPageToVersion,
        disabled: isRevertingPageToVersion || isSyncPending,
        isLoading: isRevertingPageToVersion || isSyncPending,
        tooltipText: "Restore to this version",
      };
    }

    if (!viewingVersionId && isCurrentVersion) {
      return {
        icon: <Eye size={12} />,
        onClick: handlePreviewVersion,
        disabled: true,
        isLoading: false,
        tooltipText: "You are already viewing this version",
      };
    }

    return {
      icon: <Eye size={12} />,
      onClick: handlePreviewVersion,
      disabled: isSyncPending,
      isLoading: isSyncPending,
      tooltipText: "Preview this version",
    };
  };

  const buttonState = getButtonState();

  return (
    <Tooltip content={buttonState.tooltipText}>
      <Button
        icon
        onClick={(e) => {
          e.stopPropagation();
          void buttonState.onClick();
        }}
        variant="tertiary"
        size="sm"
        disabled={buttonState.disabled}
        loading={buttonState.isLoading}
      >
        {buttonState.icon}
      </Button>
    </Tooltip>
  );
};
